import Highcharts from "highcharts";
import { color } from "../..";

const HANDLE_COLOR = color.lightNitrogen500_Main;
const BORDER_SIZE = 2;
const HANDLE_BORDER_SIZE = 2;

function getCornerAttributes(delta: number): Highcharts.SVGAttributes {
  const length = delta / 2;
  return {
    d: `M0,0 L${length},0 L${length},${BORDER_SIZE} L${BORDER_SIZE},${BORDER_SIZE} L${BORDER_SIZE},${length} L0,${length} Z`,
    stroke: color.carbon900,
    fill: HANDLE_COLOR,
    "stroke-width": 0,
  };
}

interface Position {
  x: number;
  y: number;
}

class SelectionMarker {
  x: number;
  y: number;
  width: number;
  height: number;
  delta: number;

  chart: Highcharts.Chart;
  group: Highcharts.SVGElement;
  rect: Highcharts.SVGElement;

  cornerNW: Highcharts.SVGElement;
  cornerNE: Highcharts.SVGElement;
  cornerSW: Highcharts.SVGElement;
  cornerSE: Highcharts.SVGElement;

  handle1: Highcharts.SVGElement;
  handle2: Highcharts.SVGElement;

  constructor(chart: Highcharts.Chart, delta: number) {
    this.chart = chart;
    this.x = chart.plotLeft;
    this.y = chart.plotTop;
    this.width = chart.plotWidth;
    this.height = chart.plotHeight;
    this.delta = delta;

    this.group = chart.renderer.g().attr({ zIndex: 7 }).add();
    this.rect = this.chart.renderer
      .rect(this.x, this.y, this.width, this.height, 0)
      .attr({
        class: "highcharts-selection-marker",
        fill: Highcharts.Color.parse(color.raw.lightNitrogen500_Main).setOpacity(0.15).get(),
      })
      .add(this.group);

    this.cornerNW = this.chart.renderer.path({ ...getCornerAttributes(delta) }).add(this.group);
    this.cornerNE = this.chart.renderer
      .path({ ...getCornerAttributes(delta), rotation: 90 })
      .add(this.group);
    this.cornerSE = this.chart.renderer
      .path({ ...getCornerAttributes(delta), rotation: 180 })
      .add(this.group);
    this.cornerSW = this.chart.renderer
      .path({ ...getCornerAttributes(delta), rotation: 270 })
      .add(this.group);

    this.handle1 = this.chart.renderer
      .rect(this.x, this.y, HANDLE_BORDER_SIZE, 2 * delta)
      .attr({ fill: HANDLE_COLOR })
      .add(this.group);

    this.handle2 = this.chart.renderer
      .rect(this.x, this.y, HANDLE_BORDER_SIZE, 2 * delta)
      .attr({ fill: HANDLE_COLOR })
      .add(this.group);
  }

  renderCorners(zoomXActive: boolean, zoomYActive: boolean) {
    const hasCorners = zoomXActive && zoomYActive;
    const cornersOpacity = hasCorners ? 1 : 0;
    const halfBorder = BORDER_SIZE / 2;

    this.cornerNW.attr({
      translateX: this.x - halfBorder,
      translateY: this.y - halfBorder,
      "fill-opacity": cornersOpacity,
    });
    this.cornerNE.attr({
      translateX: this.x + halfBorder + this.width,
      translateY: this.y - halfBorder,
      "fill-opacity": cornersOpacity,
    });
    this.cornerSE.attr({
      translateX: this.x + halfBorder + this.width,
      translateY: this.y + halfBorder + this.height,
      "fill-opacity": cornersOpacity,
    });
    this.cornerSW.attr({
      translateX: this.x - halfBorder,
      translateY: this.y + halfBorder + this.height,
      "fill-opacity": cornersOpacity,
    });
  }

  renderHandles(zoomXActive: boolean, zoomYActive: boolean, mouseDown: Position) {
    const hasHandles = zoomXActive !== zoomYActive;
    const handlesOpacity = hasHandles ? 1 : 0;
    const halfBorder = HANDLE_BORDER_SIZE / 2;

    const yWhenXActive = Math.max(mouseDown.y - this.delta, this.y);
    const xWhenYActive = Math.max(mouseDown.x - this.delta, this.x);

    const height = zoomXActive
      ? Math.min(mouseDown.y + this.delta, this.chart.plotTop + this.chart.plotHeight) -
        yWhenXActive
      : Math.min(mouseDown.x + this.delta, this.chart.plotLeft + this.chart.plotWidth) -
        xWhenYActive;

    this.handle1.attr({
      x: zoomXActive ? this.x - halfBorder : xWhenYActive,
      y: zoomYActive ? this.y + halfBorder : yWhenXActive,
      height,
      rotation: zoomYActive ? -90 : 0,
      "fill-opacity": handlesOpacity,
    });
    this.handle2.attr({
      x: zoomXActive ? this.x + this.width - halfBorder : xWhenYActive,
      y: zoomYActive ? this.y + this.height + halfBorder : yWhenXActive,
      height,
      rotation: zoomYActive ? -90 : 0,
      "fill-opacity": handlesOpacity,
    });
  }

  render(mouseDown: Position, currentMouse: Position, zoomXActive: boolean, zoomYActive: boolean) {
    if (zoomXActive) {
      const deltaX = currentMouse.x - mouseDown.x;
      this.width = Math.abs(deltaX);
      this.x = (deltaX > 0 ? 0 : deltaX) + mouseDown.x;

      if (!zoomYActive) {
        this.height = this.chart.plotHeight;
        this.y = this.chart.plotTop;
      }
    }
    // adjust the height of the selection marker
    if (zoomYActive) {
      const deltaY = currentMouse.y - mouseDown.y;
      this.height = Math.abs(deltaY);
      this.y = (deltaY > 0 ? 0 : deltaY) + mouseDown.y;

      if (!zoomXActive) {
        this.width = this.chart.plotWidth;
        this.x = this.chart.plotLeft;
      }
    }

    this.renderCorners(zoomXActive, zoomYActive);
    this.renderHandles(zoomXActive, zoomYActive, mouseDown);

    this.rect.attr({
      x: this.x,
      y: this.y,
      width: this.width,
      height: this.height,
    });
  }

  destroy() {
    this.group.destroy();
  }
}

export default SelectionMarker;
